/**
 * Встроенный браузер модуля: аккаунт площадки во вкладке оболочки.
 *
 * Вход подставляется сам: пара уходит оболочке (reveal → setSectionLogin)
 * и в интерфейсе не показывается. У каждого аккаунта своя вкладка — её
 * стартовый адрес помечен id аккаунта.
 *
 * Кнопки над страницей — то, ради чего браузер встроен, а не открыт рядом:
 * переписка с хозяевами и поддержка в том же аккаунте, и запись квартиры или
 * брони прямо со страницы — адрес и название берутся из открытой вкладки.
 *
 * Автозапись брони (оболочка 0.5.0+): когда вкладка доходит до страницы
 * подтверждения или деталей поездки, снимок страницы уходит на сервер, и тот
 * заводит черновик — с датами, суммой, кодом, квартирой и перепиской.
 * Менеджера и работника указывает человек: над страницей появляется
 * плашка «Заполнить», форма открывается здесь же, без ухода из браузера.
 *
 * Связь с базой: на странице квартиры или брони сверху — её карточка из
 * общей базы (статус, оценка, брони) с переходом в карточку; квартиры нет —
 * кнопка «В базу». Чёрный список виден до того, как нажата «Забронировать».
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BookmarkPlus,
  CalendarCheck,
  CalendarDays,
  CalendarPlus,
  CircleAlert,
  Link2,
  KeyRound,
  LifeBuoy,
  Luggage,
  MessageCircle,
  MonitorSmartphone,
  RotateCw,
  X,
} from 'lucide-react';
import type { StayAccountDto, StayBookingDto, StayPersonDto, StayPropertyLookupDto } from '@tw/shared-types';
import { Button } from '@/components/ui/button';
import { desktop, isDesktop, type DesktopTabInfo } from '../../lib/desktop.ts';
import { useSession } from '../../state/session.tsx';
import { describeError } from '../../lib/errors.ts';
import { cn } from '@/lib/utils';
import { EmptyState, PlatformMark, PropertyThumb, RatingBadge, StatusPill } from './bits.tsx';
import {
  PLATFORM,
  cleanTitle,
  formatDate,
  formatMoney,
  formatRange,
  isBookingPageUrl,
  isListingUrl,
  platformOf,
  plural,
} from './meta.ts';
import { PropertyDialog } from './PropertyDialog.tsx';
import { SavePropertyDialog } from './SavePropertyDialog.tsx';
import { BookingDialog, type BookingPrefill } from './BookingDialog.tsx';

interface Props {
  accounts: StayAccountDto[];
  /** Для формы черновика: кого выбрать в «Подбирал». */
  people: StayPersonDto[];
  /** Какой аккаунт открыть (выбор сохраняется между заходами в модуль). */
  accountId: string | null;
  onAccountChange: (id: string) => void;
  /** Адрес, который попросили открыть из таблицы или базы. */
  pendingUrl: string | null;
  onPendingUsed: () => void;
  onBook: (prefill: BookingPrefill) => void;
}

/**
 * Когда снимать страницу брони после загрузки, мс. Площадки дорисовывают
 * страницу скриптами уже после события загрузки, поэтому первый снимок —
 * с задержкой, а если в нём ещё нет кода или дат — ещё две попытки.
 */
const CAPTURE_DELAYS = [1500, 4000, 9000];

type CaptureNotice =
  | { kind: 'created' | 'updated' | 'unchanged' | 'filled'; booking: StayBookingDto }
  | { kind: 'incomplete' }
  | { kind: 'old-shell' };

/** Стартовый адрес аккаунта: тот же, что сервер отдаёт в reveal. */
const startUrlFor = (a: StayAccountDto) =>
  a.platform === 'AIRBNB'
    ? `https://www.airbnb.com/login?tw_account=${a.id}`
    : `https://account.booking.com/sign-in?tw_account=${a.id}`;

export function StaysBrowser({ accounts, people, accountId, onAccountChange, pendingUrl, onPendingUsed, onBook }: Props) {
  const { api, can } = useSession();
  const bridge = desktop();
  const holder = useRef<HTMLDivElement | null>(null);
  const [tabs, setTabs] = useState<DesktopTabInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<{
    url: string;
    title: string;
    photoUrl?: string | null;
    city?: string | null;
    country?: string | null;
  } | null>(null);
  const [notice, setNotice] = useState<CaptureNotice | null>(null);
  /** Аккаунт, чья вкладка уже показана: ссылку из таблицы открываем только в ней. */
  const [shownAccount, setShownAccount] = useState<string | null>(null);
  const [filling, setFilling] = useState<StayBookingDto | null>(null);
  /** Карточка базы для открытой страницы — и для какого адреса она найдена. */
  const [card, setCard] = useState<{ url: string; result: StayPropertyLookupDto } | null>(null);
  const [cardOpen, setCardOpen] = useState<string | null>(null);
  const [lookupTick, setLookupTick] = useState(0);
  /** Сколько раз снята страница брони — по «аккаунт + адрес» (см. автозапись ниже). */
  const attempts = useRef(new Map<string, number>());

  const usable = accounts.filter((a) => a.enabled);
  const account = usable.find((a) => a.id === accountId) ?? usable[0] ?? null;
  const startUrl = account ? startUrlFor(account) : null;

  /*
   * Показ вкладки аккаунта. В оболочке 0.4.0+ — со своими cookie у каждого
   * аккаунта; в старой — в общей сессии сотрудника (тогда два аккаунта одной
   * площадки вытесняют друг друга, и интерфейс об этом предупреждает).
   */
  const show = useCallback(
    (url: string) => {
      if (!bridge || !account) return;
      if (bridge.showIsolated) bridge.showIsolated(url, account.id);
      else bridge.showSection(url, url);
      setShownAccount(account.id);
    },
    [bridge, account],
  );

  // Пара для автовхода — до показа вкладки, иначе форма входа успеет
  // загрузиться без неё и подстановка сработает только после перезагрузки.
  useEffect(() => {
    if (!bridge || !account || !startUrl) return;
    let cancelled = false;
    setError(null);
    if (account.hasPassword) {
      api
        .revealStayAccount(account.id)
        .then((c) => {
          if (cancelled) return;
          bridge.setSectionLogin?.({ url: c.startUrl, login: c.login, password: c.password, twoStep: true });
          show(c.startUrl);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(describeError(err));
          show(startUrl);
        });
    } else {
      show(startUrl);
    }
    return () => {
      cancelled = true;
      setShownAccount(null);
      bridge.hideTabs();
    };
  }, [api, bridge, account, startUrl, show]);

  useEffect(() => {
    if (!bridge) return;
    return bridge.onTabs(setTabs);
  }, [bridge]);

  // Страница поверх области: оболочка рисует вкладку ровно в этом прямоугольнике.
  useEffect(() => {
    const node = holder.current;
    if (!bridge || !node) return;
    const report = (): void => {
      const r = node.getBoundingClientRect();
      bridge.setTabBounds({ x: r.left, y: r.top, width: r.width, height: r.height });
    };
    report();
    const observer = new ResizeObserver(report);
    observer.observe(node);
    window.addEventListener('scroll', report, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', report, true);
    };
  }, [bridge, account?.id]);

  /*
   * Ссылка из таблицы броней или базы. Открывается, когда показана вкладка
   * выбранного аккаунта: при переключении аккаунта сначала идёт запрос пары
   * для автовхода, и переход раньше этого ушёл бы во вкладку прежнего.
   */
  useEffect(() => {
    if (!bridge || !pendingUrl || !account || shownAccount !== account.id) return;
    const t = setTimeout(() => {
      bridge.navigateTab(pendingUrl);
      onPendingUsed();
    }, 300);
    return () => clearTimeout(t);
  }, [bridge, pendingUrl, onPendingUsed, account, shownAccount]);

  /*
   * Диалог над страницей площадки. Оболочка рисует страницу поверх окна, и
   * диалог под ней был бы не виден, — на время диалога страница убирается и
   * возвращается после. Только на переходах: первый показ делает эффект выше,
   * уже с парой для автовхода.
   */
  const dialogWasOpen = useRef(false);
  useEffect(() => {
    if (!bridge || !startUrl) return;
    const open = saving !== null || filling !== null || cardOpen !== null;
    if (open) bridge.hideTabs();
    else if (dialogWasOpen.current) show(startUrl);
    dialogWasOpen.current = open;
  }, [bridge, startUrl, saving, filling, cardOpen, show]);

  /** Данные страницы: из оболочки 0.4.0+ (с фото и городом) или из вкладки. */
  const capture = async () => {
    const fromShell = await bridge?.capturePage?.().catch(() => null);
    if (fromShell) return { ...fromShell, title: cleanTitle(fromShell.title) };
    return active ? { url: active.url, title: cleanTitle(active.title), image: null, city: null, country: null } : null;
  };

  const saveToBase = async () => {
    const page = await capture();
    if (page) setSaving({ url: page.url, title: page.title, photoUrl: page.image, city: page.city, country: page.country });
  };

  /**
   * «Записать бронь» со страницы брони (квитанция, детали, подтверждение):
   * снимок уходит на сервер как при автозаписи, но без проверки адреса — и
   * форма открывается здесь же, заполненной. Если брони на странице не
   * нашлось — обычная форма, как раньше.
   */
  const bookFromBookingPage = async (): Promise<boolean> => {
    if (!account || !bridge?.capturePage) return false;
    const page = await bridge.capturePage().catch(() => null);
    if (!page || page.text === undefined) return false;
    try {
      const res = await api.captureStayBooking({
        accountId: account.id,
        url: page.url,
        title: page.title,
        text: page.text,
        jsonLd: page.jsonLd ?? [],
        links: page.links ?? [],
        headings: page.headings ?? [],
        city: page.city,
        country: page.country,
        manual: true,
      });
      if (!res.booking) {
        if (res.result === 'incomplete') setNotice({ kind: 'incomplete' });
        return false;
      }
      attempts.current.set(`${account.id} ${page.url}`, CAPTURE_DELAYS.length);
      setLookupTick((n) => n + 1);
      if (res.booking.draft && res.booking.canEdit) setFilling(res.booking);
      else setNotice({ kind: res.result as 'created' | 'updated' | 'unchanged', booking: res.booking });
      return true;
    } catch (err) {
      setError(describeError(err));
      return false;
    }
  };

  /**
   * «Записать бронь» со страницы квартиры: квартира сразу уходит в базу (с
   * фото и городом, если оболочка их достала) и подставляется в форму. Не
   * вышло — форма получит хотя бы ссылку и название.
   */
  const bookFromPage = async () => {
    if (!account) return;
    if (!onListing) {
      if (await bookFromBookingPage()) return;
      return onBook({ accountId: account.id });
    }
    const page = await capture();
    if (!page) return onBook({ accountId: account.id });
    try {
      const property = await api.saveStayProperty({
        url: page.url,
        title: page.title,
        city: page.city,
        country: page.country,
        photoUrl: page.image,
      });
      onBook({ accountId: account.id, property });
    } catch {
      onBook({ accountId: account.id, url: page.url, title: page.title });
    }
  };

  const active = useMemo(() => tabs.find((t) => t.active) ?? null, [tabs]);
  const onListing = !!active && isListingUrl(active.url);

  /*
   * Автозапись. Срабатывает на адрес страницы брони после её загрузки — и
   * при переходе по ссылке, и при смене адреса внутри одностраничного сайта.
   * Попытки считаются по адресу: одна и та же страница снимается не больше
   * CAPTURE_DELAYS.length раз, сколько бы раз оболочка ни сообщала о вкладке.
   */
  const oldShellNoticed = useRef(false);
  const [retry, setRetry] = useState(0);
  const activeUrl = active?.url ?? null;
  const activeLoading = active?.loading ?? true;
  const canBook = can('stays.book');
  useEffect(() => {
    if (!bridge?.capturePage || !account || !canBook || !activeUrl || activeLoading) return;
    if (!isBookingPageUrl(activeUrl) || platformOf(activeUrl) !== account.platform) return;
    const key = `${account.id} ${activeUrl}`;
    const done = attempts.current.get(key) ?? 0;
    if (done >= CAPTURE_DELAYS.length) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      const page = await bridge.capturePage!().catch(() => null);
      if (cancelled || !page || page.url !== activeUrl) return;
      if (page.text === undefined) {
        // Оболочка до 0.5.0 текста страницы не отдаёт — сказать один раз.
        attempts.current.set(key, CAPTURE_DELAYS.length);
        if (!oldShellNoticed.current) setNotice({ kind: 'old-shell' });
        oldShellNoticed.current = true;
        return;
      }
      attempts.current.set(key, done + 1);
      try {
        const res = await api.captureStayBooking({
          accountId: account.id,
          url: page.url,
          title: page.title,
          text: page.text,
          jsonLd: page.jsonLd ?? [],
          links: page.links ?? [],
          headings: page.headings ?? [],
          city: page.city,
          country: page.country,
        });
        if (cancelled) return;
        if (res.booking) {
          attempts.current.set(key, CAPTURE_DELAYS.length);
          setNotice({ kind: res.result as 'created' | 'updated' | 'unchanged', booking: res.booking });
          setLookupTick((n) => n + 1);
        } else if (done + 1 < CAPTURE_DELAYS.length) {
          setRetry((n) => n + 1);
        } else if (res.result === 'incomplete') {
          setNotice({ kind: 'incomplete' });
        }
      } catch {
        // Сеть или сервер — автозапись тихо не случилась, ручная кнопка рядом.
      }
    }, CAPTURE_DELAYS[done]);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, bridge, account, canBook, activeUrl, activeLoading, retry]);

  /*
   * Карточка базы для открытой страницы. Ищется на каждой странице площадки
   * после загрузки: по ссылке на объявление, по странице брони (квитанция),
   * по названию у карточки без ссылки. С задержкой — адрес вкладки меняется
   * несколько раз за переход.
   */
  const activeTitle = active?.title ?? '';
  useEffect(() => {
    if (!activeUrl || activeLoading || !platformOf(activeUrl)) {
      setCard(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .lookupStayProperty(activeUrl, cleanTitle(activeTitle) || undefined)
        .then((result) => !cancelled && setCard({ url: activeUrl, result }))
        .catch(() => !cancelled && setCard(null));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, activeUrl, activeLoading, activeTitle, lookupTick]);

  /** Привязать ссылку открытого объявления к карточке, найденной по названию. */
  const linkCard = async (propertyId: string, url: string) => {
    try {
      await api.updateStayProperty(propertyId, { url });
      setLookupTick((n) => n + 1);
    } catch (err) {
      setError(describeError(err));
    }
  };

  if (!isDesktop() || !bridge) {
    return (
      <EmptyState icon={<MonitorSmartphone className="size-6" />} title="Браузер — в приложении BUCOR WORKSPACE">
        Аккаунты Airbnb и Booking открываются со входом без пароля только в настольном приложении. Здесь, в обычном браузере,
        доступны брони и база квартир.
      </EmptyState>
    );
  }

  if (!account) {
    return (
      <EmptyState icon={<KeyRound className="size-6" />} title="Нет доступных аккаунтов">
        {can('stays.account.manage')
          ? 'Подключите аккаунт Airbnb или Booking во вкладке «Аккаунты».'
          : 'Попросите администратора выдать вам доступ к аккаунту Airbnb или Booking.'}
      </EmptyState>
    );
  }

  const platform = PLATFORM[account.platform];

  return (
    <div className="flex h-full min-h-[520px] flex-col gap-2.5">
      {/* ── Аккаунты ── */}
      <div className="flex flex-wrap items-center gap-1.5">
        {usable.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onAccountChange(a.id)}
            className={cn(
              'inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-[12px] font-medium transition-colors',
              a.id === account.id
                ? 'border-transparent bg-heading text-card shadow-sm'
                : 'border-border bg-card text-soft hover:border-brand hover:text-brand',
            )}
          >
            <PlatformMark platform={a.platform} size="sm" />
            {a.title}
          </button>
        ))}
      </div>

      {/* ── Панель над страницей ── */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-border bg-card px-2 py-1.5 shadow-sm">
        <IconButton label="Назад" disabled={!active?.canGoBack} onClick={() => bridge.goBack()}>
          <ArrowLeft className="size-4" />
        </IconButton>
        <IconButton label="Вперёд" disabled={!active?.canGoForward} onClick={() => bridge.goForward()}>
          <ArrowRight className="size-4" />
        </IconButton>
        <IconButton label="Обновить" onClick={() => bridge.reloadTab()}>
          <RotateCw className={cn('size-4', active?.loading && 'animate-spin')} />
        </IconButton>
        <span className="mx-1 min-w-0 flex-1 truncate rounded-lg bg-secondary px-3 py-1.5 text-[12px] text-muted-foreground" title={active?.url}>
          {active?.url ?? platform.home}
        </span>
        <ToolButton icon={<MessageCircle className="size-4" />} onClick={() => bridge.navigateTab(platform.messages)}>
          Переписка
        </ToolButton>
        <ToolButton icon={<Luggage className="size-4" />} onClick={() => bridge.navigateTab(platform.trips)}>
          Брони на сайте
        </ToolButton>
        <ToolButton icon={<LifeBuoy className="size-4" />} onClick={() => bridge.navigateTab(platform.support)}>
          Поддержка
        </ToolButton>
        {can('stays.book') && (
          <>
            <span className="mx-0.5 h-5 w-px bg-border" />
            <ToolButton
              icon={<BookmarkPlus className="size-4" />}
              disabled={!onListing}
              title={onListing ? 'Сохранить эту квартиру в базу' : 'Откройте страницу квартиры'}
              onClick={() => void saveToBase()}
            >
              В базу
            </ToolButton>
            <Button
              size="sm"
              className="h-8 rounded-full"
              title="Записать бронь: со страницы брони подставится всё, со страницы квартиры — ссылка и название"
              onClick={() => void bookFromPage()}
            >
              <CalendarPlus className="size-4" /> Записать бронь
            </Button>
          </>
        )}
      </div>

      {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{error}</p>}

      {card && card.url === activeUrl && (card.result.property || onListing) && (
        <PropertyBanner
          lookup={card.result}
          onListing={onListing}
          canBook={canBook}
          onOpen={(id) => setCardOpen(id)}
          onSave={() => void saveToBase()}
          onLink={(id) => void linkCard(id, card.url)}
        />
      )}

      {notice && (
        <CaptureBanner
          notice={notice}
          onFill={(b) => setFilling(b)}
          onBookManually={() => {
            setNotice(null);
            onBook({ accountId: account.id });
          }}
          onClose={() => setNotice(null)}
        />
      )}

      {!bridge.showIsolated && usable.filter((a) => a.platform === account.platform).length > 1 && (
        <p className="rounded-xl bg-warning/10 px-3 py-2 text-[12px] text-warning">
          В этой версии приложения аккаунты одной площадки делят вход: переключившись, проверьте, в какой аккаунт вы вошли.
          Обновите BUCOR WORKSPACE до 0.4.0 — там у каждого аккаунта свой вход.
        </p>
      )}

      {/* Здесь оболочка рисует страницу площадки. */}
      <div ref={holder} className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-border bg-card shadow-md" />

      <SavePropertyDialog
        open={saving !== null}
        initial={saving}
        onClose={() => setSaving(null)}
        onSaved={() => {
          setSaving(null);
          setLookupTick((n) => n + 1);
        }}
      />

      <PropertyDialog
        propertyId={cardOpen}
        onClose={() => setCardOpen(null)}
        onChanged={() => setLookupTick((n) => n + 1)}
        onBook={(property) => {
          setCardOpen(null);
          onBook({ accountId: account.id, property });
        }}
        onOpenUrl={(url) => {
          setCardOpen(null);
          bridge.navigateTab(url);
        }}
      />

      <BookingDialog
        open={filling !== null}
        booking={filling}
        accounts={accounts}
        people={people}
        onClose={() => setFilling(null)}
        onSaved={(b) => {
          setFilling(null);
          setNotice({ kind: b.draft ? 'unchanged' : 'filled', booking: b });
        }}
      />
    </div>
  );
}

function IconButton({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-8 place-items-center rounded-lg border-0 bg-transparent p-0 text-soft transition-colors hover:bg-secondary hover:text-heading disabled:opacity-35"
    >
      {children}
    </button>
  );
}

function ToolButton({
  icon,
  onClick,
  disabled,
  title,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-8 items-center gap-1.5 rounded-full border-0 bg-transparent px-2.5 py-0 text-[12px] font-medium text-soft transition-colors hover:bg-secondary hover:text-brand disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-soft"
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Плашка автозаписи над страницей площадки. Не диалог: страница остаётся
 * видна, и менеджер может дозаполнить бронь сразу или позже из таблицы.
 */
function CaptureBanner({
  notice,
  onFill,
  onBookManually,
  onClose,
}: {
  notice: CaptureNotice;
  onFill: (b: StayBookingDto) => void;
  onBookManually: () => void;
  onClose: () => void;
}) {
  let tone: 'success' | 'warning' | 'info' = 'info';
  let text: React.ReactNode;
  let action: React.ReactNode = null;

  if (notice.kind === 'old-shell') {
    text = 'Автозапись броней работает с версии приложения 0.5.0 — обновите BUCOR WORKSPACE. Пока бронь можно записать кнопкой «Записать бронь».';
  } else if (notice.kind === 'incomplete') {
    tone = 'warning';
    text = 'Это страница брони, но даты распознать не получилось. Запишите бронь вручную.';
    action = (
      <Button size="sm" variant="outline" className="h-7 rounded-full" onClick={onBookManually}>
        Записать бронь
      </Button>
    );
  } else {
    const b = notice.booking;
    const summary = (
      <>
        <span className="font-semibold">{b.title}</span>
        {' · '}
        {formatRange(b.checkIn, b.checkOut)}
        {b.priceMinor !== null && <> · {formatMoney(b.priceMinor, b.currency)}</>}
      </>
    );
    if (notice.kind === 'filled') {
      tone = 'success';
      text = <>Бронь записана: {summary}</>;
    } else if (b.draft) {
      tone = 'warning';
      text = (
        <>
          {notice.kind === 'created' ? 'Бронь записана автоматически' : 'Эта бронь записана черновиком'}: {summary}
          {b.status === 'CANCELLED' && <span className="text-destructive"> · отменена</span>}
          . Укажите, кто подбирал и кто заселяется.
        </>
      );
      if (b.canEdit) {
        action = (
          <Button size="sm" className="h-7 rounded-full" onClick={() => onFill(b)}>
            Заполнить
          </Button>
        );
      }
    } else {
      tone = notice.kind === 'updated' && b.status === 'CANCELLED' ? 'warning' : 'success';
      text =
        notice.kind === 'updated' ? (
          <>
            Бронь обновлена со страницы{b.status === 'CANCELLED' ? ' — отменена на сайте' : ''}: {summary}
          </>
        ) : (
          <>Эта бронь уже в таблице: {summary}</>
        );
    }
  }

  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-2.5 rounded-xl border px-3 py-2 text-[13px]',
        tone === 'success' && 'border-success/30 bg-success/10 text-heading',
        tone === 'warning' && 'border-warning/40 bg-warning/10 text-heading',
        tone === 'info' && 'border-border bg-secondary/60 text-soft',
      )}
    >
      {tone === 'warning' ? (
        <CircleAlert className="size-4 flex-none text-warning" />
      ) : (
        <CalendarCheck className={cn('size-4 flex-none', tone === 'success' ? 'text-success' : 'text-muted-foreground')} />
      )}
      <p className="min-w-0 flex-1 leading-snug">{text}</p>
      {action}
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        className="grid size-7 place-items-center rounded-lg border-0 bg-transparent p-0 text-muted-foreground hover:bg-secondary hover:text-heading"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

/**
 * Карточка базы над страницей площадки: что компания знает об этой
 * квартире. Чёрный список — красным и с причиной, до кнопки «Забронировать»
 * на сайте.
 */
function PropertyBanner({
  lookup,
  onListing,
  canBook,
  onOpen,
  onSave,
  onLink,
}: {
  lookup: StayPropertyLookupDto;
  onListing: boolean;
  canBook: boolean;
  onOpen: (id: string) => void;
  onSave: () => void;
  onLink: (id: string) => void;
}) {
  const p = lookup.property;
  if (!p) {
    return (
      <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-dashed border-border bg-card px-3 py-2 text-[13px] text-soft">
        <CircleAlert className="size-4 flex-none text-muted-foreground" />
        <p className="min-w-0 flex-1">Этой квартиры нет в базе — ни отзывов, ни броней компании.</p>
        {canBook && (
          <Button size="sm" variant="outline" className="h-7 rounded-full" onClick={onSave}>
            В базу
          </Button>
        )}
      </div>
    );
  }
  const black = p.status === 'BLACKLISTED';
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-xl border px-2.5 py-2 text-[13px]',
        black ? 'border-destructive/40 bg-destructive/10' : p.status === 'VERIFIED' ? 'border-success/30 bg-success/10' : 'border-border bg-card',
      )}
    >
      <PropertyThumb propertyId={p.id} photoUrl={p.photoUrl} platform={p.platform} className="size-10 flex-none rounded-lg" />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2">
          <span className="max-w-[360px] truncate font-semibold text-heading">{p.title}</span>
          <StatusPill status={p.status} />
          <RatingBadge rating={p.rating} count={p.ratingCount} />
        </p>
        <p className={cn('mt-0.5 flex flex-wrap items-center gap-x-3 text-[12px]', black ? 'text-destructive' : 'text-muted-foreground')}>
          {black && <span className="font-medium">{p.statusNote ?? 'В чёрном списке — загляните в комментарии'}</span>}
          {!black && p.statusNote && <span>{p.statusNote}</span>}
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <CalendarDays className="size-3.5" />
            {p.bookingCount > 0
              ? `${p.bookingCount} ${plural(p.bookingCount, 'бронь', 'брони', 'броней')} · последняя ${formatDate(p.lastBookedAt!)}`
              : 'компания ещё не бронировала'}
          </span>
          {lookup.matchedBy === 'title' && <span className="text-warning">найдена по названию — проверьте, та ли это квартира</span>}
        </p>
      </div>
      {lookup.matchedBy === 'title' && onListing && canBook && (
        <Button size="sm" variant="outline" className="h-7 rounded-full" onClick={() => onLink(p.id)}>
          <Link2 className="size-3.5" /> Привязать ссылку
        </Button>
      )}
      <Button size="sm" variant={black ? 'destructive' : 'default'} className="h-7 rounded-full" onClick={() => onOpen(p.id)}>
        Карточка
      </Button>
    </div>
  );
}
